import React, { ReactNode } from 'react';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import FilterPlates from 'components/MetricsInfoButton/MetricInfoPopup/FilterPlates';
import Tooltip from 'components/Tooltip';

import { FILTER_SOURCE_LABELS } from './constants';
import { useOpenMetric } from './MetricViewerProvider';

// What is actually inside a report: every metric, dimension and filter.
//
// Shared by the roster row and the suggestion cards, because the question they
// ask is the same one — "should this be in the agent's hands?" — and answering
// it from two differently-shaped summaries would be needless work.
//
// `core` is called out per metric: it is the account's own blessed pick for a
// concept, and on an account with duplicate names it is the single best signal
// for which of two same-named metrics is the one to keep. Each metric links
// into its definition, because deciding between two of them means reading both.

const DetailDiv = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border-secondary);
`;

const ColumnsDiv = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
`;

const ColumnDiv = styled.div`
  min-width: 220px;
  flex: 1;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconSpan = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const Section = ({
  title,
  items,
  emptyText,
}: {
  title: string;
  // `label` is the linkable part; `detail` is plain text that follows it. Only
  // the metric NAME opens anything — the dataset, status and filters beside it
  // are there to be read, and making them clickable would imply each opens
  // something of its own.
  items: {
    key: string;
    label: string;
    detail?: string;
    // Rendered inline, straight after the label — the metric's filter icon.
    afterLabel?: ReactNode;
    // A small trailing affordance (e.g. "+ test"), right of the detail.
    action?: ReactNode;
    isMuted?: boolean;
    onClick?: () => void;
  }[];
  emptyText: string;
}) => (
  <ColumnDiv>
    <Typography.Body type="Body 13 Bold">
      {title} ({items.length})
    </Typography.Body>
    {items.length === 0 && (
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        {emptyText}
      </Typography.Body>
    )}
    {items.map((item) => (
      <ItemRow key={item.key}>
        <Typography.Body
          type="Body 12"
          isLink={Boolean(item.onClick)}
          onClick={item.onClick}
          color={
            item.isMuted
              ? 'var(--color-text-secondary)'
              : 'var(--color-text-primary)'
          }
        >
          {item.label}
        </Typography.Body>
        {item.afterLabel}
        {item.detail && (
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            {` · ${item.detail}`}
          </Typography.Body>
        )}
        {item.action}
      </ItemRow>
    ))}
  </ColumnDiv>
);

// Everything about a metric EXCEPT its name and its filters — those are plated
// separately. On an account with duplicate names this line plus the plates are
// the whole of the difference between two otherwise identical entries.
const metricDetail = (metric: AiReady.MetricSummary): string =>
  [
    metric.status === 'core' ? 'core' : undefined,
    metric.datasets.length ? metric.datasets.join(', ') : undefined,
    metric.savedFilterNames.length
      ? metric.savedFilterNames.join(' and ')
      : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

const TooltipPlatesDiv = styled.div`
  padding: 4px;
`;

// A filter icon, only on metrics that have filters, with the plates on hover.
//
// Inline rather than listed: most metrics on a report carry a filter or two,
// and plating every one of them turned a scannable column of names into a wall.
// The icon keeps "this one is scoped" visible at a glance and puts the detail
// one hover away — which is the right trade for a list you read top to bottom.
const MetricFilterIcon = ({ metric }: { metric: AiReady.MetricSummary }) => {
  if (!metric.filters || Object.keys(metric.filters).length === 0) {
    return null;
  }
  return (
    <IconSpan>
      <Tooltip
        isFlexAnchor
        testId={`ai-ready-metric-filters-${metric.id}`}
        content={
          <TooltipPlatesDiv>
            <FilterPlates filters={metric.filters} />
          </TooltipPlatesDiv>
        }
      >
        <Icon icon="filters" height={12} width={12} />
      </Tooltip>
    </IconSpan>
  );
};

const ReportDetail = ({
  report,
  onAddTestForMetric,
}: {
  report: AiReady.AnalysedReport;
  onAddTestForMetric?: (metric: AiReady.MetricSummary) => void;
}) => {
  const openMetric = useOpenMetric();

  return (
    <DetailDiv data-testid={`ai-ready-detail-${report.reportId}`}>
      {report.agentDescription && (
        <div style={{ marginBottom: 12 }}>
          <Typography.Body type="Body 12">
            {report.agentDescription}
          </Typography.Body>
        </div>
      )}
      <ColumnsDiv>
        <Section
          title="Metrics"
          emptyText="None — the agent has nothing to query from this report."
          items={report.metrics.map((metric) => ({
            key: metric.id,
            label: metric.isMissing ? metric.id : metric.name,
            detail: metric.isMissing ? 'missing' : metricDetail(metric),
            afterLabel: <MetricFilterIcon metric={metric} />,
            isMuted: metric.isMissing,
            // A metric line can seed a golden-question test with itself as the
            // expected metric — the failure we have evidence for.
            ...(onAddTestForMetric && !metric.isMissing
              ? {
                  action: (
                    <Button
                      type="Ghost"
                      size="Small"
                      label="+ test"
                      testId={`ai-ready-add-test-metric-${metric.id}`}
                      onClick={() => onAddTestForMetric(metric)}
                    />
                  ),
                }
              : {}),
            // A special metric has no definition to open, and a missing id has
            // no metric at all.
            ...(metric.isMissing || metric.isSpecial
              ? {}
              : { onClick: () => openMetric(metric.id) }),
          }))}
        />
        <Section
          title="Dimensions"
          emptyText="None — nothing on this report is broken down."
          items={report.dimensions.map((dimension) => ({
            key: dimension.field,
            label: dimension.label,
          }))}
        />
        <Section
          title="Filters"
          emptyText="None."
          items={report.filters.map((filter) => ({
            key: filter.key,
            label: `${FILTER_SOURCE_LABELS[filter.source]}: ${
              filter.description
            }`,
            ...(filter.isAgentReproducible
              ? {}
              : { detail: 'agent cannot express this' }),
            isMuted: !filter.isAgentReproducible,
          }))}
        />
      </ColumnsDiv>
    </DetailDiv>
  );
};

export default ReportDetail;
